/**
 * Public landing page — shown when the visitor has no identity yet.
 *
 * Visual: big Gamez hero, dice + chess-piece accents, dark gradient
 * background. Copy stays unassuming about the encrypted-chat layer —
 * this is a board-games site that happens to have chat baked in.
 */
import { useState } from 'react';
import type { ChatClient } from '../client/api.js';

export interface LandingPageProps {
  readonly client: ChatClient;
  readonly onSubmitted?: (email: string) => void;
  /**
   * Called when the user uploads a previously-downloaded backup
   * bundle. Owner is App.tsx (holds the raw keystore). After a
   * successful import the app should transition to the unlock
   * flow so the passphrase can decrypt the restored entries.
   */
  readonly onImportBackup?: (file: File) => Promise<void>;
}

export function LandingPage({ client, onSubmitted, onImportBackup }: LandingPageProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [importError, setImportError] = useState<string | undefined>();
  const [importing, setImporting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setStatus('sending');
    setError(undefined);
    try {
      await client.requestRegistration(email.trim().toLowerCase());
      setStatus('done');
      onSubmitted?.(email.trim().toLowerCase());
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'failed to submit');
    }
  }

  return (
    <main data-testid="landing" className="landing">
      <section className="landing-hero">
        <div className="landing-pieces" aria-hidden="true">
          <span className="piece p1">♞</span>
          <span className="piece p2">♜</span>
          <span className="piece p3">♟</span>
          <span className="piece p4">🎲</span>
          <span className="piece p5">♛</span>
        </div>
        <h1 className="landing-title">
          GAME<span className="z">Z</span>
        </h1>
        <p className="landing-tag">
          Pocket-sized board games with friends — play a match, chat while you wait, all end-to-end
          encrypted in your browser.
        </p>
      </section>

      <section className="landing-card">
        {status === 'done' ? (
          <div data-testid="landing-done">
            <h2>Check your inbox</h2>
            <p className="faint">
              We'll email a sign-in link once an operator approves your request. This can take a few
              hours — you can close this tab.
            </p>
          </div>
        ) : (
          <form onSubmit={(e) => void submit(e)} data-testid="landing-form" className="vstack">
            <label>
              <span className="landing-label">request an invite</span>
              <input
                data-testid="landing-email"
                type="email"
                required
                value={email}
                placeholder="you@example.com"
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
            </label>
            <button
              data-testid="landing-submit"
              type="submit"
              className="primary landing-submit"
              disabled={status === 'sending'}
            >
              {status === 'sending' ? 'sending…' : 'send request →'}
            </button>
            {error && (
              <p data-testid="landing-error" style={{ color: 'var(--danger)' }}>
                {error}
              </p>
            )}
          </form>
        )}
      </section>

      {onImportBackup && (
        <section className="landing-card">
          <p className="faint" style={{ fontSize: '0.9em' }}>
            Already have a <strong>Gamez backup file</strong>? Restore it here, then unlock with
            your passphrase.
          </p>
          <label className="primary landing-submit" style={{ display: 'inline-block' }}>
            {importing ? 'restoring…' : 'restore from backup'}
            <input
              type="file"
              accept="application/json,.json"
              data-testid="landing-import-backup"
              style={{ display: 'none' }}
              disabled={importing}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                setImporting(true);
                setImportError(undefined);
                void (async () => {
                  try {
                    await onImportBackup(file);
                  } catch (err) {
                    setImportError(err instanceof Error ? err.message : 'restore failed');
                  } finally {
                    setImporting(false);
                  }
                })();
              }}
            />
          </label>
          {importError && (
            <p data-testid="landing-import-error" style={{ color: 'var(--danger)' }}>
              {importError}
            </p>
          )}
        </section>
      )}

      <footer className="landing-foot faint">
        <span>E2E encrypted · MLS · WebRTC video ·</span>{' '}
        <a
          href="https://github.com/puchkarev/chat"
          target="_blank"
          rel="noopener noreferrer"
          className="landing-link"
        >
          source
        </a>
      </footer>
    </main>
  );
}
